import React, { useState, useRef } from 'react'
import { Row, Col } from 'antd'

import {
  CheckListDetail,
  QCType,
  JobType,
  SubJobType,
  DetailType,
  Function,
} from 'features/checklist/unit/types'

import QCTypeBox from './QCTypeBox'
import JobBox from './JobBox'
import SubJobBox from './SubJobBox'
import DetailBox from './DetailBox'
import DetailConfigBox from './DetailConfigBox'
import Modal, { ModalHandles } from './Modal/Modal'

export interface SelectType {
  qcType: number
  job: number
  subJob: number
  detail: number
}

interface Props {
  isActive: boolean
  checkListDetail: CheckListDetail
  updateCheckList: () => void
  qCTypeList: QCType[]
  functionList: Function[]
  onSave: () => void
}

const QCList: React.ComponentType<Props> = ({
  isActive,
  checkListDetail,
  updateCheckList,
  qCTypeList,
  functionList,
  onSave,
}) => {
  const isUsed = checkListDetail.Status.toLocaleLowerCase() === 'used'
  const [selected, setSelected] = useState<SelectType>({
    qcType: 0,
    job: 0,
    subJob: 0,
    detail: 0,
  })
  const modalRef = useRef<ModalHandles>(null)

  const toggleModal = (tab: number) => {
    if (modalRef.current) {
      modalRef.current.showModal(tab)
    }
  }

  const onQcType = (i: number) => {
    selected.qcType = i
    setSelected({ ...selected })
  }

  const onJob = (i: number) => {
    selected.job = i
    setSelected({ ...selected })
  }

  const onSubJob = (i: number) => {
    selected.subJob = i
    setSelected({ ...selected })
  }

  const onDetail = (i: number) => {
    selected.detail = i
    setSelected({ ...selected })
  }

  const getQcType = () => {
    if (
      selected.qcType !== -1 &&
      selected.qcType < checkListDetail.QCTypeList.length
    ) {
      return checkListDetail.QCTypeList[selected.qcType]
    }

    return undefined
  }

  const getJob = (item: QCType | undefined) => {
    if (
      item &&
      selected.job !== -1 &&
      item.JobTypeList &&
      selected.job < item.JobTypeList.length
    ) {
      return item.JobTypeList[selected.job]
    }

    return undefined
  }

  const getSubJob = (item: JobType | undefined) => {
    if (
      item &&
      selected.subJob !== -1 &&
      selected.subJob < item.SubJobTypeList.length
    ) {
      return item.SubJobTypeList[selected.subJob]
    }

    return undefined
  }

  const getDetail = (item: SubJobType | undefined) => {
    if (
      item &&
      selected.detail !== -1 &&
      selected.detail < item.DetailList.length
    ) {
      return item.DetailList[selected.detail]
    }

    return undefined
  }
  const qcTypeItem: QCType | undefined = getQcType()
  const jobItem: JobType | undefined = getJob(qcTypeItem)
  const subJobItem: SubJobType | undefined = getSubJob(jobItem)
  const detailItem: DetailType | undefined = getDetail(subJobItem)

  const addQcType = (qc: QCType, index?: number) => {
    const obj = checkListDetail.QCTypeList

    if (index != undefined) {
      obj[index] = qc
    } else {
      obj.push(qc)
    }
    updateCheckList()
  }

  const addJob = (job: JobType, index?: number) => {
    const obj = checkListDetail.QCTypeList[selected.qcType].JobTypeList

    if (index != undefined) {
      obj[index] = job
    } else {
      obj.push(job)
    }
    updateCheckList()
  }

  const addSubJob = (subjob: SubJobType, index?: number) => {
    const obj =
      checkListDetail.QCTypeList[selected.qcType].JobTypeList[selected.job]
        .SubJobTypeList

    if (index != undefined) {
      obj[index] = subjob
    } else {
      obj.push(subjob)
    }
    updateCheckList()
  }

  const addDetail = (detail: DetailType, index?: number) => {
    const obj =
      checkListDetail.QCTypeList[selected.qcType].JobTypeList[selected.job]
        .SubJobTypeList[selected.subJob].DetailList

    if (index != undefined) {
      obj[index] = detail
    } else {
      obj.push(detail)
    }
    updateCheckList()
  }

  const deleteQc = (it: QCType) => {
    checkListDetail.QCTypeList = checkListDetail.QCTypeList.filter(
      qc => qc.QCTypeID !== it.QCTypeID
    )
    updateCheckList()
  }

  const deleteJob = (it: JobType) => {
    if (qcTypeItem) {
      qcTypeItem.JobTypeList = qcTypeItem.JobTypeList.filter(
        job => job.JobTypeName !== it.JobTypeName
      )
      updateCheckList()
    }
  }

  const deleteSubJob = (it: SubJobType) => {
    if (jobItem) {
      jobItem.SubJobTypeList = jobItem.SubJobTypeList.filter(
        job => job.SubJobTypeName !== it.SubJobTypeName
      )
      updateCheckList()
    }
  }

  const deleteDetail = (it: DetailType) => {
    if (subJobItem) {
      subJobItem.DetailList = subJobItem.DetailList.filter(
        job => job.DetailName !== it.DetailName
      )
      updateCheckList()
    }
  }

  const openModalEdit = (obj: any, index: number, tab: number) => {
    if (modalRef.current) {
      modalRef.current.showModal(
        tab,
        obj,
        index,
        qcTypeItem ? qcTypeItem.QCTypeName : undefined
      )
    }
  }

  return (
    <div style={{ display: isActive ? '' : 'none' }}>
      <Row type="flex" justify="space-between">
        <Col span={8} style={css}>
          <QCTypeBox
            item={checkListDetail}
            selected={selected.qcType}
            onSelected={onQcType}
            qCTypeList={qCTypeList}
            showModal={toggleModal}
            onDelete={deleteQc}
            openModalEdit={openModalEdit}
            disabled={isUsed}
          />
        </Col>

        <Col span={8} style={css}>
          <JobBox
            item={qcTypeItem}
            selected={selected.job}
            onSelected={onJob}
            showModal={toggleModal}
            onDelete={deleteJob}
            openModalEdit={openModalEdit}
            disabled={isUsed}
          />
        </Col>

        <Col span={8}>
          <SubJobBox
            item={jobItem}
            selected={selected.subJob}
            onSelected={onSubJob}
            showModal={toggleModal}
            onDelete={deleteSubJob}
            openModalEdit={openModalEdit}
            disabled={isUsed}
          />
        </Col>
      </Row>

      <Row type="flex" justify="space-between">
        <Col span={8} style={css}>
          <DetailBox
            item={subJobItem}
            selected={selected.detail}
            onSelected={onDetail}
            showModal={toggleModal}
            onDelete={deleteDetail}
            openModalEdit={openModalEdit}
            disabled={isUsed}
          />
        </Col>

        <Col span={16}>
          <DetailConfigBox
            item={detailItem}
            criteriaList={checkListDetail.CriteriaList}
            updateCheckList={updateCheckList}
            onSave={onSave}
            disabled={isUsed}
          />
        </Col>
      </Row>
      <Modal
        selected={selected}
        checkListDetail={checkListDetail}
        qCTypeList={qCTypeList}
        functionList={functionList}
        addQcType={addQcType}
        addJob={addJob}
        addSubJob={addSubJob}
        addDetail={addDetail}
        ref={modalRef}
        disabled={isUsed}
      />
    </div>
  )
}

const css = {
  paddingRight: '16px',
  marginBottom: '16px',
}
export default QCList
